import type { SuggestionChip } from '../../../lib/websocket/events';

/**
 * SuggestionChipsRow — Fase 1 «um cérebro só» (T9).
 *
 * Linha de chips de conduta que o dry-run do conductor sugeriu no turno
 * (o que a IA FARIA e o operador aprova). Compartilhada entre a superfície
 * REAL da sugestão no Composer (SuggestionStrip + ghost text) e a
 * AiSuggestionStrip (strip rica canonical r7b).
 *
 * Regra de honestidade (padrão `--static` do AlertRow [3.600]): um chip só
 * vira `<button>` quando o CALLBACK do fluxo existente foi fornecido pelo
 * caller — `otp-challenge` → `onOtpChallenge` (fluxo Confirmar titular),
 * `close` → `onCloseConversation` (ação Encerrar) e `link-customer` →
 * `onLinkCustomer` (F2-b: abre o EstablishLinkModal para estabelecer o
 * vínculo do cliente que declarou vínculo mas está sem participação). Sem
 * callback (ação indisponível p/ este operador/estado — ex.: sem a cap
 * `customer-link-establish`) ou kind informativo (`escalate`, `write-tool`,
 * `transition` reservado) → badge estático com tooltip, que NÃO finge ser
 * clicável. Nenhum caminho de execução novo nasce aqui.
 *
 * Sem chips (sugestão legado v1 / turno só-texto) → retorna null (a
 * superfície fica byte-idêntica à atual).
 */
export function SuggestionChipsRow({
  chips,
  onOtpChallenge,
  onCloseConversation,
  onLinkCustomer,
  className,
  testId = 'suggestion-chips-row',
}: {
  /** `undefined` (sugestão v1 / turno só-texto) ou `[]` → não renderiza nada. */
  chips: SuggestionChip[] | undefined;
  /** Liga o chip `otp-challenge` ao fluxo "Confirmar titular" da tela. */
  onOtpChallenge?: () => void;
  /** Liga o chip `close` à ação "Encerrar" da tela. */
  onCloseConversation?: () => void;
  /** Liga o chip `link-customer` (F2-b) ao fluxo "Estabelecer vínculo". */
  onLinkCustomer?: () => void;
  className?: string;
  testId?: string;
}): JSX.Element | null {
  if (chips === undefined || chips.length === 0) return null;
  return (
    <div className={className} data-testid={testId}>
      {chips.map((chip, i) => {
        const onClick =
          chip.kind === 'otp-challenge'
            ? onOtpChallenge
            : chip.kind === 'close'
              ? onCloseConversation
              : chip.kind === 'link-customer'
                ? onLinkCustomer
                : undefined;
        if (onClick !== undefined) {
          return (
            <button
              key={i}
              type="button"
              className="chip chip--action"
              onClick={onClick}
              title={chip.label}
              data-chip-kind={chip.kind}
            >
              {chip.label}
            </button>
          );
        }
        // Informativo (escalate / write-tool / transition reservado — ou ação
        // indisponível): badge estático, não finge ser clicável.
        return (
          <span
            key={i}
            className="badge badge--neutral"
            title={chip.label}
            data-chip-kind={chip.kind}
          >
            {chip.label}
          </span>
        );
      })}
    </div>
  );
}
